/**
 * LocalPostFab — floating "Post here" action button pinned to the
 * bottom-left of location article pages.
 *
 * Why: the Community Hub is now at the top, but users still scroll
 * through the long-form article.  This FAB gives them a one-tap way
 * to post a Pulse / Event / Cause **for this specific location**
 * without scrolling back up.
 *
 * Behaviour:
 *   - Signed-in users → expands a mini menu with 3 destinations,
 *     each carrying `?city=<city>` so the target composer can pre-fill.
 *   - Guests → collapses menu and goes to /register with a redirect
 *     back to the current article, matching the existing "Join to
 *     post here" pattern in CommunityHub.
 *
 * Positioning:
 *   - bottom-left (bottom-6 left-6) to avoid the AI Helper FAB
 *     which owns bottom-right.
 */
import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Plus, X, RadioTower, CalendarDays, HeartHandshake } from "lucide-react";

const MENU_ITEMS = [
  {
    key: "pulse",
    label: "Post a Pulse",
    hint: "Share what's happening",
    Icon: RadioTower,
    to: (city) => `/pulse?city=${encodeURIComponent(city || "")}`,
    accent: "#4338CA",
  },
  {
    key: "event",
    label: "Add an Event",
    hint: "Festival, meetup, gathering",
    Icon: CalendarDays,
    to: (city) => `/events/submit?city=${encodeURIComponent(city || "")}`,
    accent: "#0891B2",
  },
  {
    key: "cause",
    label: "Submit a Cause",
    hint: "Rally help for someone",
    Icon: HeartHandshake,
    to: (city) => `/submit-cause?city=${encodeURIComponent(city || "")}`,
    accent: "#DB2777",
  },
];

export default function LocalPostFab({ city }) {
  const { user } = useAuth();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  // Collapse when clicking outside the popover.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("touchstart", onDocClick);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("touchstart", onDocClick);
    };
  }, [open]);

  // Auto-close on route change (belt-and-braces; menu clicks navigate away).
  useEffect(() => { setOpen(false); }, [loc.pathname]);

  const guestTarget = `/register?redirect=${encodeURIComponent(loc.pathname + loc.search)}`;

  return (
    <div
      ref={rootRef}
      className="fixed bottom-6 left-6 z-50"
      data-testid="local-post-fab-root"
    >
      {/* Expanded menu */}
      {open && (
        <div
          className="mb-3 w-64 rounded-2xl border shadow-2xl overflow-hidden"
          style={{
            background: "white",
            borderColor: "rgba(0,0,0,0.08)",
          }}
          data-testid="local-post-fab-menu"
        >
          <div
            className="px-4 py-3 border-b"
            style={{
              borderColor: "rgba(0,0,0,0.06)",
              background: "linear-gradient(135deg, #EEF2FF 0%, #FDF4FF 100%)",
            }}
          >
            <div className="text-xs font-mono uppercase tracking-widest opacity-60">
              Post to
            </div>
            <div className="text-sm font-semibold truncate" title={city}>
              {city || "this location"}
            </div>
          </div>

          {user ? (
            <ul className="py-1">
              {MENU_ITEMS.map(({ key, label, hint, Icon, to, accent }) => (
                <li key={key}>
                  <Link
                    to={to(city)}
                    className="flex items-center gap-3 px-4 py-3 hover:bg-black/[0.04] transition"
                    data-testid={`local-post-fab-${key}`}
                  >
                    <div
                      className="w-9 h-9 rounded-full flex items-center justify-center shrink-0"
                      style={{ background: `${accent}1A`, color: accent }}
                    >
                      <Icon className="w-4.5 h-4.5" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-medium leading-tight">{label}</div>
                      <div className="text-xs opacity-60 leading-tight mt-0.5">{hint}</div>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            <div className="p-4">
              <p className="text-sm mb-3" style={{ color: "#1E1B4B" }}>
                Join Social Pulse to post a Pulse, Event, or Cause in{" "}
                <strong>{city || "this location"}</strong>.
              </p>
              <Link
                to={guestTarget}
                className="wofy-btn-primary w-full justify-center text-sm"
                data-testid="local-post-fab-guest-join"
              >
                Join — takes ~30 seconds
              </Link>
            </div>
          )}
        </div>
      )}

      {/* Trigger button */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label={open ? "Close post menu" : "Post here"}
        className="group flex items-center gap-2 rounded-full pl-4 pr-5 py-3 shadow-lg transition-transform duration-200 hover:scale-105 active:scale-95"
        style={{
          background: "linear-gradient(135deg, #4338CA 0%, #7C3AED 100%)",
          color: "white",
          boxShadow: "0 10px 30px -8px rgba(67,56,202,0.55)",
        }}
        data-testid="local-post-fab-trigger"
      >
        {open ? (
          <X className="w-5 h-5" />
        ) : (
          <Plus className="w-5 h-5" />
        )}
        <span className="text-sm font-semibold whitespace-nowrap">
          {open ? "Close" : "Post here"}
        </span>
      </button>
    </div>
  );
}
